import React, { useEffect, useState, useMemo, useRef } from "react";
import { api } from "../lib/api";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, Legend,
} from "recharts";
import { Activity, Circle, RefreshCw, Signal, FileDown } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { cn } from "../lib/utils";
import { fmtMDT } from "../lib/time";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const STATUS_TONE = {
  up: "bg-emerald-500",
  degraded: "bg-amber-500",
  down: "bg-rose-500",
  paused: "bg-muted-foreground/50",
  unknown: "bg-muted-foreground/40",
};

// Uptime% → SLA tone (green ≥ 99.9, yellow 99.5–99.9, red < 99.5)
const uptimeTone = (pct) => {
  if (typeof pct !== "number") return { pill: "border-border bg-muted/40 text-muted-foreground", dot: "bg-muted-foreground/40" };
  if (pct < 99.5) return { pill: "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300", dot: "bg-rose-500" };
  if (pct < 99.9) return { pill: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300", dot: "bg-amber-500" };
  return { pill: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300", dot: "bg-emerald-500" };
};

// Red tone used when a probe is down or not yet connected to Pingdom.
const DOWN_TONE = {
  pill: "border-rose-500/60 bg-rose-500/20 text-rose-700 dark:text-rose-200",
  dot: "bg-rose-500",
};

// Format seconds → "5m" / "1h 23m" / "0s" — matches SystemStatusCard formatting.
const fmtDowntime = (sec) => {
  if (sec == null || Number.isNaN(sec)) return "—";
  if (sec <= 0) return "0s";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m`;
  return `${s}s`;
};

// Distinct palette for per-probe lines. Cycles if there are more than 10 probes.
const PROBE_COLORS = [
  "#49C22E", // brand
  "#ec4899", // pink
  "#10b981", // emerald
  "#f59e0b", // amber
  "#8b5cf6", // violet
  "#0ea5e9", // sky
  "#ef4444", // rose
  "#22c55e", // green
  "#eab308", // yellow
  "#14b8a6", // teal
];
const CORE_FAILOVER_COLOR = "#111827"; // near-black, thick line
const CORE_PROBES = ["yow core", "yyc core"]; // matched case-insensitively against display name

const RANGE_OPTIONS = [
  { key: "today",     label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d",        label: "Past 7 days" },
  { key: "30d",       label: "Past 30 days" },
  { key: "90d",       label: "Past quarter" },
  { key: "365d",      label: "Past year" },
  { key: "ytd",       label: "Year to date" },
  { key: "custom",    label: "Custom range…" },
];

export default function UptimeHistoryCard() {
  const [history, setHistory] = useState(null);
  const [checks, setChecks] = useState(null);
  const [probeHistory, setProbeHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangeKey, setRangeKey] = useState("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [appliedCustom, setAppliedCustom] = useState({ from: "", to: "" });
  const [refreshToken, setRefreshToken] = useState(0);
  const [exporting, setExporting] = useState(false);
  const chartRef = useRef(null);

  useEffect(() => {
    // Per-probe daily uptime for the multi-line chart. Reacts to range/refresh.
    let cancelled = false;
    (async () => {
      try {
        let params = `range=${encodeURIComponent(rangeKey)}`;
        if (rangeKey === "custom") {
          if (!appliedCustom.from || !appliedCustom.to) return;
          params += `&date_from=${encodeURIComponent(appliedCustom.from)}&date_to=${encodeURIComponent(appliedCustom.to)}`;
        }
        if (refreshToken > 0) params += "&force=true";
        const { data } = await api.get(`/uptime/probe-history?${params}`);
        if (!cancelled) setProbeHistory(data);
      } catch { if (!cancelled) setProbeHistory(null); }
    })();
    return () => { cancelled = true; };
  }, [rangeKey, appliedCustom, refreshToken]);

  useEffect(() => {
    // History fetch reacts to range/refresh — fast (single Mongo aggregation).
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        let params = `range=${encodeURIComponent(rangeKey)}`;
        if (rangeKey === "custom") {
          if (!appliedCustom.from || !appliedCustom.to) { setLoading(false); return; }
          params += `&date_from=${encodeURIComponent(appliedCustom.from)}&date_to=${encodeURIComponent(appliedCustom.to)}`;
        }
        const { data } = await api.get(`/uptime/history?${params}`);
        if (!cancelled) setHistory(data);
      } catch { if (!cancelled) setHistory(null); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [rangeKey, appliedCustom, refreshToken]);

  useEffect(() => {
    // Pingdom probe list is independent of the selected range — it always shows the
    // live 30-day rollup from Pingdom. Kept in its own effect because the call
    // fans out to N sub-requests and can take several seconds.
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/uptime/pingdom-checks");
        if (!cancelled) setChecks(data);
      } catch { if (!cancelled) setChecks(null); }
    })();
    return () => { cancelled = true; };
  }, [refreshToken]);

  // Fetch the same grouped probe list the Global Status Bar uses so the pills
  // below the history chart appear in identical order (DC Network → Calgary →
  // Ottawa → Montreal) and include every configured probe — not just the ones
  // Pingdom has already connected.
  const [statusGroups, setStatusGroups] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/status/summary?window=30d");
        if (!cancelled) setStatusGroups(data?.groups || []);
      } catch { if (!cancelled) setStatusGroups(null); }
    })();
    return () => { cancelled = true; };
  }, [refreshToken]);

  const series = useMemo(() => (history?.series || []).map((r) => ({
    label: r.label,
    uptime: r.combined ?? r.pingdom ?? r.internal,
  })), [history]);
  const hasHistory = series.some((r) => typeof r.uptime === "number");

  // Fall back to a synthesised flat series from the last-known Pingdom average when we have
  // no heartbeat rows yet (e.g. fresh install or long custom windows).
  const fallbackAvg = useMemo(() => {
    const rows = (checks?.checks || []).filter((c) => typeof c.uptime_30d_pct === "number");
    if (rows.length === 0) return null;
    return rows.reduce((a, b) => a + b.uptime_30d_pct, 0) / rows.length;
  }, [checks]);
  const displaySeries = hasHistory
    ? series
    : (fallbackAvg != null ? series.map((r) => ({ ...r, uptime: fallbackAvg })) : series);

  // Wide-format multi-line series: one column per probe + a `_core` failover column
  // that = max(YOW Core, YYC Core) at each slice (per user's ask — failover means the
  // higher of the two cores at each time slice is the effective truth).
  const multiSeries = useMemo(() => {
    const probes = probeHistory?.probes || [];
    if (probes.length === 0) return [];
    const days = new Map(); // day -> row { label, day, <probeName>: uptime, _core?: ... }
    probes.forEach((p) => {
      (p.series || []).forEach((s) => {
        const row = days.get(s.day) || { day: s.day, label: s.label };
        row[p.name] = s.uptime;
        days.set(s.day, row);
      });
    });
    const rows = Array.from(days.values()).sort((a, b) => (a.day < b.day ? -1 : 1));
    rows.forEach((row) => {
      const yow = row["YOW Core"];
      const yyc = row["YYC Core"];
      const vals = [yow, yyc].filter((v) => typeof v === "number");
      if (vals.length > 0) row._core = Math.max(...vals);
    });
    return rows;
  }, [probeHistory]);

  const probeNames = useMemo(
    () => (probeHistory?.probes || []).map((p) => p.name),
    [probeHistory],
  );

  const changeRange = (rk) => {
    setRangeKey(rk);
    if (rk !== "custom") {
      // Clear any prior custom window so we truly re-fetch on rk change.
      setAppliedCustom({ from: "", to: "" });
    }
  };

  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    setAppliedCustom({ from: customFrom, to: customTo });
  };

  const refresh = () => setRefreshToken((t) => t + 1);

  const rangeLabel = history?.range_label
    || RANGE_OPTIONS.find((r) => r.key === rangeKey)?.label
    || "Past 30 days";

  const summaryStats = useMemo(() => {
    const rows = displaySeries.filter((r) => typeof r.uptime === "number");
    if (rows.length === 0) return null;
    const values = rows.map((r) => r.uptime);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    return {
      avg: avg.toFixed(3),
      min: Math.min(...values).toFixed(3),
      max: Math.max(...values).toFixed(3),
      samples: values.length,
    };
  }, [displaySeries]);

  const checksList = checks?.checks || [];
  const activeCount = checksList.length;
  const upCount = checksList.filter((c) => c.status === "up").length;

  const exportCsv = () => {
    // Two-section CSV: metadata header, daily series, then per-probe table.
    // Excel and Sheets both handle blank-row-separated blocks cleanly.
    const now = new Date();
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [];
    lines.push(escape("UC Vigil — Uptime Report"));
    lines.push([escape("Range"), escape(rangeLabel)].join(","));
    if (history?.start_iso) lines.push([escape("Window start"), escape(new Date(history.start_iso).toISOString())].join(","));
    if (history?.end_iso)   lines.push([escape("Window end"),   escape(new Date(history.end_iso).toISOString())].join(","));
    lines.push([escape("Generated at"), escape(now.toISOString())].join(","));
    lines.push([escape("Source"), escape("Pingdom.com — externally verified probe telemetry")].join(","));
    lines.push([escape("Attestation"), escape("Includes Planned Maintenance with failover")].join(","));
    if (summaryStats) {
      lines.push("");
      lines.push([escape("Average uptime (%)"), escape("Best day (%)"), escape("Worst day (%)"), escape("Days sampled")].join(","));
      lines.push([summaryStats.avg, summaryStats.max, summaryStats.min, summaryStats.samples].join(","));
    }

    lines.push("");
    lines.push([escape("Date"), escape("Combined uptime (%)"), escape("Internal (%)"), escape("Pingdom (%)")].join(","));
    (history?.series || []).forEach((r) => {
      lines.push([escape(r.day), escape(r.combined ?? ""), escape(r.internal ?? ""), escape(r.pingdom ?? "")].join(","));
    });

    if (checksList.length > 0) {
      lines.push("");
      lines.push([
        escape("Probe"), escape("Status"), escape("Uptime 30d (%)"),
        escape("Last response (ms)"), escape("Outages 30d"),
      ].join(","));
      checksList.forEach((c) => {
        lines.push([
          escape(c.name),
          escape(c.status || ""),
          escape(typeof c.uptime_30d_pct === "number" ? c.uptime_30d_pct.toFixed(3) : ""),
          escape(typeof c.last_response_ms === "number" ? c.last_response_ms : ""),
          escape(c.outages_30d ?? 0),
        ].join(","));
      });
    }

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = now.toISOString().slice(0, 10);
    a.href = url;
    a.download = `uc-vigil-uptime-${rangeKey}-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const doc = new jsPDF({ unit: "pt", format: "letter" });
      const now = new Date();
      const line = { fontSize: 10, textColor: 90 };

      doc.setFontSize(18);
      doc.text("UC Vigil — Uptime Report", 40, 50);

      doc.setFontSize(11);
      doc.setTextColor(30);
      doc.text(`Reporting window: ${rangeLabel}`, 40, 72);
      if (history?.start_iso && history?.end_iso) {
        doc.setFontSize(10);
        doc.setTextColor(line.textColor);
        doc.text(
          `${fmtMDT(history.start_iso)}  →  ${fmtMDT(history.end_iso)}`,
          40, 88,
        );
      }
      doc.setFontSize(9);
      doc.setTextColor(line.textColor);
      doc.text(`Generated ${fmtMDT(now)} · UC Vigil`, 40, 104);

      // Attestation banner
      doc.setDrawColor(95, 142, 237);
      doc.setFillColor(235, 242, 253);
      doc.roundedRect(40, 118, 532, 44, 6, 6, "FD");
      doc.setTextColor(30);
      doc.setFontSize(10);
      doc.text("Data sourced from Pingdom.com — externally verified probe telemetry.", 52, 136);
      doc.setTextColor(80);
      doc.setFontSize(9);
      doc.text("Includes Planned Maintenance with failover.", 52, 152);

      // Try to embed the on-screen chart as an image
      let chartY = 178;
      try {
        const svg = chartRef.current?.querySelector("svg");
        if (svg) {
          const clone = svg.cloneNode(true);
          const w = svg.clientWidth || 800;
          const h = svg.clientHeight || 300;
          clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
          clone.setAttribute("width", w);
          clone.setAttribute("height", h);
          const svgStr = new XMLSerializer().serializeToString(clone);
          const svgBlob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
          const url = URL.createObjectURL(svgBlob);
          const img = new Image();
          img.crossOrigin = "anonymous";
          await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
          const canvas = document.createElement("canvas");
          canvas.width = w * 2; canvas.height = h * 2;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          URL.revokeObjectURL(url);
          const png = canvas.toDataURL("image/png");
          const pdfW = 532;
          const pdfH = pdfW * (h / w);
          doc.addImage(png, "PNG", 40, chartY, pdfW, pdfH);
          chartY += pdfH + 18;
        }
      } catch { /* embedding is best-effort */ }

      // Summary stats
      if (summaryStats) {
        autoTable(doc, {
          startY: chartY,
          head: [["Average uptime", "Best day", "Worst day", "Days sampled"]],
          body: [[
            `${summaryStats.avg}%`,
            `${summaryStats.max}%`,
            `${summaryStats.min}%`,
            summaryStats.samples,
          ]],
          styles: { fontSize: 10 },
          headStyles: { fillColor: [95, 142, 237], textColor: 255 },
          margin: { left: 40, right: 40 },
        });
        chartY = doc.lastAutoTable.finalY + 18;
      }

      // Per-probe table
      if (checksList.length > 0) {
        autoTable(doc, {
          startY: chartY,
          head: [["Probe", "Status", "Uptime (30d)", "Response", "Outages (30d)"]],
          body: checksList.map((c) => [
            c.name,
            c.status || "—",
            typeof c.uptime_30d_pct === "number" ? `${c.uptime_30d_pct.toFixed(3)}%` : "—",
            typeof c.last_response_ms === "number" ? `${c.last_response_ms} ms` : "—",
            c.outages_30d ?? 0,
          ]),
          styles: { fontSize: 9 },
          headStyles: { fillColor: [95, 142, 237], textColor: 255 },
          margin: { left: 40, right: 40 },
        });
      }

      // Footer attestation on last page
      const pageCount = doc.getNumberOfPages();
      for (let p = 1; p <= pageCount; p++) {
        doc.setPage(p);
        doc.setFontSize(8);
        doc.setTextColor(140);
        doc.text(
          `UC Vigil · ${rangeLabel} · Includes Planned Maintenance with failover · Page ${p} of ${pageCount}`,
          40, doc.internal.pageSize.getHeight() - 20,
        );
      }

      const stamp = now.toISOString().slice(0, 10);
      doc.save(`uc-vigil-uptime-${rangeKey}-${stamp}.pdf`);
    } finally { setExporting(false); }
  };

  if (loading && !history) {
    return <div className="h-64 rounded-xl bg-muted/30 animate-pulse" />;
  }

  return (
    <section className="rounded-xl border border-border bg-card p-4" data-testid="uptime-history-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Signal className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h4 className="text-[14px] font-semibold leading-none">Uptime · {rangeLabel}</h4>
            <div className="text-[11px] text-muted-foreground mt-1">
              {history?.pingdom_configured ? "Pingdom + internal heartbeats" : "Internal heartbeats"} ·
              {" "}{upCount}/{activeCount} probes up · Includes Planned Maintenance with failover
            </div>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
          <Select value={rangeKey} onValueChange={changeRange}>
            <SelectTrigger className="h-8 w-[160px]" data-testid="uptime-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map((r) => (
                <SelectItem key={r.key} value={r.key} data-testid={`uptime-range-${r.key}`}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={refresh} className="h-8 gap-1.5" data-testid="uptime-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} className="h-8 gap-1.5" data-testid="uptime-export-csv">
            <FileDown className="w-3.5 h-3.5" /> CSV
          </Button>
          <Button size="sm" onClick={exportPdf} disabled={exporting} className="h-8 gap-1.5" data-testid="uptime-export-pdf">
            <FileDown className="w-3.5 h-3.5" /> {exporting ? "Preparing…" : "Export PDF"}
          </Button>
        </div>
      </div>

      {rangeKey === "custom" && (
        <div className="mb-3 flex items-end gap-2 flex-wrap p-3 rounded-lg border border-dashed border-border bg-muted/20">
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">From</label>
            <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-8" data-testid="uptime-custom-from" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">To</label>
            <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-8" data-testid="uptime-custom-to" />
          </div>
          <Button size="sm" onClick={applyCustom} disabled={!customFrom || !customTo} data-testid="uptime-custom-apply">Apply</Button>
        </div>
      )}

      {(multiSeries.length > 0 && probeNames.length > 0) ? (
        <div className="h-56" ref={chartRef}>
          <ResponsiveContainer>
            <LineChart data={multiSeries} margin={{ top: 4, right: 10, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={Math.max(0, Math.floor(multiSeries.length / 8))} />
              <YAxis
                domain={[(dataMin) => Math.max(90, Math.floor((dataMin ?? 100) - 0.5)), 100]}
                tickFormatter={(v) => `${v}%`}
                tick={{ fontSize: 10 }}
                width={40}
              />
              <Tooltip
                contentStyle={{ fontSize: 11 }}
                formatter={(v, name) => (typeof v === "number" ? [`${v.toFixed(3)}%`, name === "_core" ? "Core failover (max YOW/YYC)" : name] : ["—", name])}
              />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} iconType="line" formatter={(v) => v === "_core" ? "Core failover" : v} />
              <ReferenceLine y={99.9} stroke="#49C22E" strokeDasharray="4 4" opacity={0.5} />
              {probeNames.map((name, i) => (
                <Line
                  key={name}
                  type="monotone"
                  dataKey={name}
                  stroke={PROBE_COLORS[i % PROBE_COLORS.length]}
                  strokeWidth={1.5}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              ))}
              <Line
                type="monotone"
                dataKey="_core"
                stroke={CORE_FAILOVER_COLOR}
                strokeWidth={2.5}
                dot={false}
                strokeDasharray="6 3"
                isAnimationActive={false}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : displaySeries.length > 0 && displaySeries.some((r) => typeof r.uptime === "number") ? (
        <div className="h-44" ref={chartRef}>
          <ResponsiveContainer>
            <LineChart data={displaySeries} margin={{ top: 4, right: 6, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={Math.max(0, Math.floor(displaySeries.length / 8))} />
              <YAxis
                domain={[(dataMin) => Math.max(90, Math.floor((dataMin ?? 100) - 0.5)), 100]}
                tickFormatter={(v) => `${v}%`}
                tick={{ fontSize: 10 }}
                width={40}
              />
              <Tooltip
                contentStyle={{ fontSize: 11 }}
                formatter={(v) => (typeof v === "number" ? [`${v.toFixed(3)}%`, "Uptime"] : ["—", "Uptime"])}
              />
              <ReferenceLine y={99.9} stroke="#49C22E" strokeDasharray="4 4" opacity={0.5} />
              <Line type="monotone" dataKey="uptime" stroke="#49C22E" strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-44 rounded-lg border border-dashed border-border flex flex-col items-center justify-center text-center px-4">
          <Activity className="w-5 h-5 text-muted-foreground mb-2" />
          <div className="text-[13px] font-medium">Collecting heartbeat data</div>
          <div className="text-[11.5px] text-muted-foreground mt-1">
            Internal probes run every 60s — chart will populate as history accumulates.
          </div>
        </div>
      )}

      {statusGroups && statusGroups.some((g) => (g.members || []).length > 0) ? (
        <div className="mt-4">
          <div className="text-[10.5px] uppercase tracking-wide text-muted-foreground font-semibold mb-2">Pingdom probes</div>
          {(() => {
            // Flatten groups in the same order the Global Status Bar renders them.
            const flat = statusGroups.flatMap((g) => g.members || []);
            const yow = flat.find((c) => (c.name || "").toLowerCase() === "yow core");
            const yyc = flat.find((c) => (c.name || "").toLowerCase() === "yyc core");
            const pctOf = (c) => (typeof c?.uptime_pct === "number" ? c.uptime_pct : (typeof c?.uptime_30d_pct === "number" ? c.uptime_30d_pct : null));
            const coreVals = [pctOf(yow), pctOf(yyc)].filter((v) => typeof v === "number");
            const coreCombined = coreVals.length ? Math.max(...coreVals) : null;
            const coreTone = uptimeTone(coreCombined);
            return (
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-2 items-stretch">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 lg:col-span-4 gap-2">
                  {flat.map((c) => {
                    const pct = pctOf(c);
                    const downSec = c.downtime_seconds ?? c.downtime_seconds_30d;
                    // Red if the probe isn't connected in Pingdom yet, is a placeholder,
                    // isn't activated, or Pingdom reports it as down.
                    const notConnected = c.is_placeholder || c.state === "pending" || c.state === "not_activated" || pct == null;
                    const isDown = c.status === "down";
                    const tone = (notConnected || isDown) ? DOWN_TONE : uptimeTone(pct);
                    const subText = notConnected
                      ? (c.state === "not_activated" ? "not activated" : "awaiting Pingdom")
                      : `${pct.toFixed(3)}% · ${fmtDowntime(downSec)} down`;
                    return (
                      <div
                        key={c.id || c.name}
                        className={cn(
                          "rounded-lg border px-2.5 py-2 flex items-center gap-2 min-w-0 transition-colors",
                          tone.pill,
                        )}
                        data-testid={`pingdom-check-${c.id || c.name}`}
                        title={c.name}
                      >
                        <Circle className={cn("w-2 h-2 rounded-full shrink-0", tone.dot)} fill="currentColor" strokeWidth={0} />
                        <div className="min-w-0">
                          <div className="text-[12px] font-semibold truncate">{c.name}</div>
                          <div className="text-[10.5px] opacity-80 truncate">{subText}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {coreCombined != null && (
                  <div
                    className={cn(
                      "rounded-lg border-2 p-3 flex flex-col justify-center items-center text-center transition-colors min-w-0 overflow-hidden",
                      "lg:col-span-1 h-full",
                      coreTone.pill,
                    )}
                    data-testid="pingdom-check-core-combined"
                    title="Highest of YOW Core and YYC Core — represents the effective uptime with failover between DC cores."
                  >
                    <div className="flex items-center gap-1.5">
                      <Circle className={cn("w-2.5 h-2.5 rounded-full shrink-0", coreTone.dot)} fill="currentColor" strokeWidth={0} />
                      <span className="text-[13px] font-bold">Core Combined</span>
                    </div>
                    <div className="text-[clamp(20px,3vw,30px)] font-bold tabular-nums mt-2 leading-none w-full truncate">
                      {coreCombined.toFixed(3)}%
                    </div>
                    <div className="text-[10.5px] opacity-80 mt-2 w-full truncate">
                      max(YOW Core, YYC Core)
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          <div className="mt-2 flex items-center gap-3 text-[10.5px] text-muted-foreground">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> ≥ 99.9%</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> 99.5–99.9%</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500" /> &lt; 99.5% · down · not connected</span>
          </div>
        </div>
      ) : null}
    </section>
  );
}
